"use client";

import { useMemo, useState, type ReactNode } from "react";
import { useDragZoom } from "@/lib/use-drag-zoom";
import { ComposedChart, Line, ReferenceLine, ReferenceArea, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { ZoomIn } from "lucide-react";
import type { PricePoint, YearValue } from "@/lib/data/types";
import { RangeSelector, type ChartRangeLabel } from "@/components/charts/range-selector";
import { DataQualityBadge } from "@/components/kpi-table/data-quality-badge";
import { filterYearRange } from "@/lib/year-range";
import { medianOf } from "@/lib/stats";
import { formatCompactCurrency, formatPercent, formatRatio } from "@/lib/format";
import { describeTrend } from "@/lib/trend-summary";
import type { DataQualityReport } from "@/lib/data/validation/types";
import {
  computeDailyMultiple,
  computeForwardMultiplePath,
  computeAverageMultiple,
  type ForwardMultipleStep,
} from "@/lib/valuation/multiples";
import {
  computeYearTicks,
  computePriceAxisTicks,
  downsample,
  filterDateRange,
  formatPriceAxisTick,
  withTimeField,
} from "@/lib/price-chart-utils";
import { useIsNarrowViewport } from "@/lib/use-narrow-viewport";
import { cn } from "@/lib/utils";

// KPI-Zeitreihen sind (anders als taegliche Kursdaten) i.d.R. jaehrlich/quartalsweise
// granular - ein grosszuegigeres Minimum als price-history-chart.tsx' 3 Tage, damit sich
// nicht auf einen praktisch leeren Ausschnitt zoomen laesst.
const MIN_SPAN_MS = 180 * 24 * 60 * 60 * 1000; // ~6 Monate

type ValueFormat = "ratio" | "percent" | "currency";

function formatValue(format: ValueFormat, value: number, currency: string): string {
  if (format === "percent") return formatPercent(value);
  if (format === "currency") return formatCompactCurrency(value, currency);
  return formatRatio(value);
}

/** Weicher, navy-getoenter Konturschatten fuer Linien - gleicher Farbton wie die
 * bestehenden Tooltip-Schatten im Projekt (`rgba(22,38,92,...)`), unabhaengig von der
 * jeweiligen Linienfarbe, damit alle Linien gleich "schweben" statt uneinheitlich zu wirken. */
const LINE_SHADOW_STYLE = { filter: "drop-shadow(0 2px 3px rgba(22,38,92,0.18))" };

/** Moderne Pill-Legende statt einfacher Checkboxen - gleiche Bauweise wie die bestehenden
 * `RangeSelector`/`MetricToggle`-Pillen (bg-muted, rund, aktiver Zustand hebt sich per
 * Karten-Hintergrund+Farbpunkt ab), damit Ein-/Ausblenden von Linien optisch zum Rest des
 * Produkts passt statt wie ein rohes Formularelement zu wirken. */
function SeriesTogglePill({
  label,
  color,
  active,
  onClick,
  trailing,
}: {
  label: string;
  color: string;
  active: boolean;
  onClick: () => void;
  trailing?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
        active
          ? "border-transparent bg-muted text-ink"
          : "border-hairline text-muted-foreground hover:text-ink",
      )}
    >
      <span
        className="h-2 w-2 shrink-0 rounded-full transition-colors"
        style={{ backgroundColor: active ? color : "#D8D3C6" }}
      />
      {label}
      {trailing}
    </button>
  );
}

export interface MultiKpiSeriesInput {
  key: string;
  label: string;
  color: string;
  format: ValueFormat;
  data: YearValue[];
}

/**
 * Mehrere KPI-Zeitreihen als anklickbar ein-/ausblendbare Linien im selben Chart, je
 * sichtbarer Linie eine gestrichelte Median-Referenzlinie (zeigt auf einen Blick, ob der
 * aktuelle Wert ueber oder unter dem eigenen historischen Median im gewaehlten Zeitraum
 * liegt) - macht eine separate Tabellen-Median-Spalte fuer dieselben Kennzahlen redundant.
 * Nur aktuell sichtbare Linien fliessen in die automatische Y-Achsen-Skalierung ein (kein
 * gerendertes <Line> -> Recharts ignoriert den dataKey bei der Domain-Berechnung), daher
 * bleibt die Skala auch bei sehr unterschiedlichen Wertebereichen (z.B. KGV ~20 vs.
 * KBV ~3) sinnvoll lesbar, sobald man nur eine der beiden Linien einblendet. Gemeinsam von
 * Profitabilitaet- und Bewertung-Tab genutzt (vorher "ValuationMultiChart", nur fuer
 * KGV/KBV) - generischer Name, da die Komponente selbst nichts Bewertungs-Spezifisches
 * mehr enthaelt.
 *
 * X-Achse ist echte Zeit (nicht mehr das Jahr als kategoriale Zahl, Erik 2026-07-15:
 * Chart soll "tagesaktuell" bis heute reichen statt beim letzten Bilanzjahr abzubrechen) -
 * die echten Jahrespunkte bleiben linear verbunden (Harte Regel: keine geglaettete
 * Interpolation), zusaetzlich haelt eine gestufte, gestrichelte Verlaengerungslinie
 * (`type="stepAfter"`, gleiche Technik wie die Prognosezone in `DailyMultiplesChart`) den
 * letzten bekannten Jahreswert bis zum heutigen Tag sichtbar - ohne einen einzigen
 * erfundenen Zwischenwert, diese Kennzahlen aendern sich nur mit einem neuen Bericht.
 */
export function MultiKpiLineChart({
  series,
  currency = "USD",
  dataQuality,
}: {
  series: MultiKpiSeriesInput[];
  currency?: string;
  dataQuality?: DataQualityReport;
}) {
  const [range, setRange] = useState<ChartRangeLabel>("Max");
  const [visible, setVisible] = useState<Set<string>>(() => new Set(series.map((s) => s.key)));
  // Einmal beim Mount eingefroren statt bei jedem Render neu gelesen - Date.now() ist eine
  // unreine Funktion (react-hooks/purity) und wuerde bei jedem Re-Render die "bis heute"-
  // Verlaengerungslinie leicht verschieben.
  const [todayTime] = useState(() => Date.now());

  const filteredSeries = useMemo(
    () => series.map((s) => ({ ...s, data: filterYearRange(s.data, range) })),
    [series, range],
  );

  const medianBySeries = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of filteredSeries) {
      const m = medianOf(s.data);
      if (m !== undefined) map.set(s.key, m);
    }
    return map;
  }, [filteredSeries]);

  // Echte Jahrespunkte (Stichtag 31.12., gleiche Konvention wie computeDailyMultiple) plus
  // je Serie eine gestufte "bis heute"-Verlaengerung ab dem letzten bekannten Wert - kein
  // erfundener Zwischenwert, nur derselbe Wert bis zum aktuellen Datum fortgeschrieben.
  const chartData = useMemo(() => {
    const rows = new Map<number, { time: number } & Record<string, number | undefined>>();
    function upsert(time: number, patch: Record<string, number | undefined>) {
      rows.set(time, { ...(rows.get(time) ?? { time }), ...patch });
    }
    for (const s of filteredSeries) {
      const sorted = [...s.data].sort((a, b) => a.year - b.year);
      for (const point of sorted) {
        upsert(Date.UTC(point.year, 11, 31), { [s.key]: point.value });
      }
      const last = sorted[sorted.length - 1];
      if (last) {
        const lastTime = Date.UTC(last.year, 11, 31);
        if (todayTime > lastTime) {
          upsert(lastTime, { [`${s.key}__extend`]: last.value });
          upsert(todayTime, { [`${s.key}__extend`]: last.value });
        }
      }
    }
    return [...rows.values()].sort((a, b) => a.time - b.time);
  }, [filteredSeries, todayTime]);

  // Klick+Ziehen zoomt zusaetzlich zum 1J/3J/5J/10J/Max-Range-Selector in einen frei
  // waehlbaren Ausschnitt (Erik 2026-07-11, Feedback-Runde 8 Punkt 4) - gleiches Prinzip
  // wie in HistoricalLineChart: filtert die schon berechnete chartData weiter, statt die
  // Achsen-Domain separat zu verwalten. Domain ist jetzt Zeit (ms) statt Jahr. Mausrad-/
  // Tastatur-Zoom (neu, Siebzehnte autonome Charge) ueber denselben gemeinsamen Hook wie
  // die uebrigen Zeitreihen-Charts - siehe lib/use-drag-zoom.ts.
  const {
    zoomRange,
    resetZoom,
    refAreaLeft,
    refAreaRight,
    chartWrapperRef,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onKeyDown,
    keyboardA11yProps,
  } = useDragZoom({
    fullMin: chartData[0]?.time,
    fullMax: chartData[chartData.length - 1]?.time,
    minSpan: MIN_SPAN_MS,
  });

  const visibleChartData = useMemo(() => {
    if (!zoomRange) return chartData;
    const [lo, hi] = zoomRange;
    return chartData.filter((d) => d.time >= lo && d.time <= hi);
  }, [chartData, zoomRange]);

  const isNarrow = useIsNarrowViewport();
  const axisTicks = useMemo(() => {
    const asDates = visibleChartData.map((d) => ({ date: new Date(d.time).toISOString().slice(0, 10) }));
    return computeYearTicks(asDates, isNarrow ? 5 : 8).map((d) => Date.parse(d));
  }, [visibleChartData, isNarrow]);

  const yAxisWidth = useMemo(() => {
    let maxLen = 0;
    for (const s of filteredSeries) {
      for (const point of s.data) {
        maxLen = Math.max(maxLen, formatValue(s.format, point.value, currency).length);
      }
    }
    return Math.max(48, Math.min(96, maxLen * 7 + 14));
  }, [filteredSeries, currency]);

  // Achsenformat richtet sich nach der ersten sichtbaren Linie - mehrere gleichzeitig
  // sichtbare Kennzahlen mit unterschiedlichem Format (Ratio vs. Prozent) auf einer
  // gemeinsamen Achse zu mischen waere ohnehin nicht sinnvoll interpretierbar.
  const axisFormat = useMemo(
    () => series.find((s) => visible.has(s.key))?.format ?? "ratio",
    [series, visible],
  );

  function toggle(key: string) {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size === 1) return prev; // mindestens eine Linie sichtbar lassen
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  if (series.every((s) => s.data.length === 0)) {
    return <p className="text-sm text-muted-foreground">Für diese Kennzahlen liegt keine Historie vor.</p>;
  }

  const visibleSeries = series.filter((s) => visible.has(s.key));

  // Trendsaetze bewusst aus der VOLLEN Historie je sichtbarer Linie (nicht der
  // aktuell gewaehlten 1J/3J/5J/10J-Ansicht) - gleiches Prinzip wie in
  // RangedHistoricalChart.
  const trends = visibleSeries
    .map((s) => describeTrend(s.label, s.data, s.format, currency))
    .filter((t): t is string => t !== null);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {series.map((s) => (
            <SeriesTogglePill
              key={s.key}
              label={s.label}
              color={s.color}
              active={visible.has(s.key)}
              onClick={() => toggle(s.key)}
              trailing={<DataQualityBadge validation={dataQuality?.metrics.find((m) => m.metric === s.key)} />}
            />
          ))}
        </div>
        <div className="flex items-center gap-3">
          {zoomRange ? (
            <button
              type="button"
              onClick={resetZoom}
              className="text-xs font-medium text-secondary underline underline-offset-2 hover:text-ink"
            >
              Zoom zurücksetzen ({new Date(zoomRange[0]).getUTCFullYear()}–{new Date(zoomRange[1]).getUTCFullYear()})
            </button>
          ) : (
            <span className="hidden items-center gap-1 text-xs text-muted-foreground sm:inline-flex">
              <ZoomIn className="h-3 w-3" aria-hidden="true" />
              Bereich ziehen zum Zoomen
            </span>
          )}
          <RangeSelector
            value={range}
            onChange={(r) => {
              setRange(r);
              resetZoom();
            }}
          />
        </div>
      </div>
      {/* accessibilityLayer={false} unten: BUGFIX (2026-07-18) gegen den "gelben Balken"
          Browser-Fokusring beim Klicken auf den Chart, siehe ausfuehrlicher Kommentar in
          fair-value-simulator.tsx (gleiche Recharts-Ursache: tabIndex am SVG-Root). Erik
          meldete das Symptom 2026-07-22 erneut, jetzt seitenweit statt nur hier - Root-Cause-
          Fund: der Grossteil der uebrigen Charts (historical-line-chart.tsx,
          price-history-chart.tsx, technical-chart.tsx) hatte diesen Fix nie erhalten. Jetzt
          zusaetzlich global abgesichert ueber `.recharts-surface:focus:not(:focus-visible)`
          in app/globals.css, greift automatisch fuer JEDEN Chart im Projekt. */}
      <div
        ref={chartWrapperRef}
        className="h-72 w-full rounded-md outline-none [&_svg]:outline-none [&_svg]:focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onKeyDown={onKeyDown}
        {...keyboardA11yProps}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={visibleChartData}
            margin={{ top: 8, right: 24, left: 0, bottom: 0 }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            accessibilityLayer={false}
          >
            <CartesianGrid stroke="#16265C" strokeOpacity={0.12} strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="time"
              type="number"
              domain={["dataMin", "dataMax"]}
              scale="time"
              ticks={axisTicks}
              interval={0}
              tickFormatter={(v: number) => String(new Date(v).getUTCFullYear())}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              axisLine={{ stroke: "#ECEAE3" }}
              tickLine={false}
            />
            <YAxis
              domain={["auto", "auto"]}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              tickFormatter={(v: number) => formatValue(axisFormat, v, currency)}
              width={yAxisWidth}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              formatter={(value, name) => {
                const key = String(name).replace(/__extend$/, "");
                const s = series.find((item) => item.key === key);
                const suffix = String(name).endsWith("__extend") ? " (letzter bekannter Wert)" : "";
                return [
                  s ? formatValue(s.format, Number(value), currency) : String(value),
                  s ? s.label + suffix : String(name),
                ];
              }}
              labelFormatter={(label) =>
                new Date(Number(label)).toLocaleDateString("de-DE", { year: "numeric", month: "short", day: "2-digit" })
              }
              cursor={{ stroke: "#16265C", strokeOpacity: 0.25, strokeWidth: 1, strokeDasharray: "3 3" }}
              contentStyle={{
                borderRadius: 4,
                borderColor: "#ECEAE3",
                fontSize: 13,
                boxShadow: "0 4px 16px rgba(22,38,92,0.08)",
              }}
            />
            {visibleSeries.map((s) => {
              const m = medianBySeries.get(s.key);
              return m === undefined ? null : (
                <ReferenceLine
                  key={`median-${s.key}`}
                  y={m}
                  stroke={s.color}
                  strokeOpacity={0.55}
                  strokeDasharray="5 4"
                  label={{
                    value: `Median ${formatValue(s.format, m, currency)}`,
                    position: "insideTopRight",
                    fill: s.color,
                    fillOpacity: 0.85,
                    fontSize: 10,
                  }}
                />
              );
            })}
            {visibleSeries.map((s) => (
              <Line
                key={`${s.key}__extend`}
                type="stepAfter"
                dataKey={`${s.key}__extend`}
                stroke={s.color}
                strokeOpacity={0.5}
                strokeWidth={1.75}
                strokeDasharray="3 3"
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            ))}
            {visibleSeries.map((s) => (
              <Line
                key={s.key}
                type="linear"
                dataKey={s.key}
                name={s.key}
                stroke={s.color}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={false}
                activeDot={{ r: 5.5, fill: s.color, stroke: "#FAF9F5", strokeWidth: 2.5 }}
                isAnimationActive={false}
                connectNulls
                style={LINE_SHADOW_STYLE}
              />
            ))}
            {refAreaLeft !== null && refAreaRight !== null && (
              <ReferenceArea x1={refAreaLeft} x2={refAreaRight} fill="#16265C" fillOpacity={0.12} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Gestrichelt: letzter bekannter Jahreswert bis heute fortgeschrieben, keine neue
        Kennzahl - ändert sich erst mit dem nächsten Bericht.
      </p>
      {trends.length > 0 && (
        <div className="mt-2 space-y-1">
          {trends.map((t) => (
            <p key={t} className="text-xs text-muted-foreground">
              {t}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export interface DailyMultipleSeriesInput {
  key: string;
  label: string;
  color: string;
  /** Jaehrliche Kennzahl je Aktie (z.B. EPS fuer KGV, Umsatz je Aktie fuer KUV) - Basis
   * fuer computeDailyMultiple() (lib/valuation/multiples.ts, siehe D1). */
  metricSteps: YearValue[];
  /** Echte Analysten-Schaetzjahre derselben Kennzahl, fuer die Prognosezone - nur Jahre
   * mit tatsaechlicher Schaetzung, kein erfundenes drittes Jahr. */
  forwardSteps: ForwardMultipleStep[];
  /** Langfrist-Wachstumsrate fuer die Extrapolation ueber forwardSteps hinaus (optional,
   * Yahoo liefert das nicht fuer jede Aktie). */
  longTermGrowthRate?: number;
  /** Kurzer Hinweistext, falls `metricSteps` selbst schon eine Naeherung ist (z.B. KBV aus
   * Jahresgewinn/Eigenkapitalrendite abgeleitet statt direkt geliefert) - unter dem Chart
   * angezeigt, damit eine Naeherung nie unmarkiert wie eine exakte Kennzahl wirkt. */
  note?: string;
  /** TTM-Bruecke (Runde 8 Punkt 14, Erik-Beispiel Equinix): ohne diesen Wert haelt
   * computeDailyMultiple() den KGV/KUV-Wert bis zum naechsten Jahresabschluss auf der
   * letzten JAHRES-Kennzahl fest, waehrend der Fairer-Wert-Simulator (siehe
   * fair-value-simulator.tsx) fuer "aktuell" bereits Yahoos TTM-Kennzahl
   * (fundamentalsOrEmpty.eps, derselbe Standard wie ueberall sonst im Projekt) nutzt -
   * fuer Firmen mit spuerbarem Jahres-/TTM-Unterschied driften beide Anzeigen dadurch
   * auseinander. Wenn gesetzt, ergaenzt DailyMultiplesChart einen zusaetzlichen
   * Metrik-Punkt exakt am letzten echten Kurstag (nicht am Jahresende) mit diesem TTM-
   * Wert, sodass der aktuellste Chart-Punkt denselben Standard wie der Simulator nutzt. */
  currentMetricValue?: number;
}

interface DailyChartRow {
  time: number;
  [key: string]: number | undefined;
}

const FORECAST_YEARS = 3;

/**
 * Tagesgenaue Multiples-Reihe mit Prognosezone (backlog.md -> D2 "Multiples-Chart mit
 * Tages-Granularitaet") - eigenstaendige, im FASTgraphs-Stil gezackte Ergaenzung zu
 * `MultiKpiLineChart` oben (die bleibt fuer Profitabilitaet-/andere Jahres-KPI-Charts
 * unveraendert bestehen, kein Breaking Change). Bewusst auf KGV/KUV/KBV begrenzt (nicht die
 * urspruenglich diskutierten weiteren Kennzahlen inkl. KGV bilanziert/KCV) - fuer eine
 * getrennte GAAP-EPS-Reihe bzw. eine historische Kurs/FCF-Multiple-Reihe fehlt die
 * Datengrundlage, dieselbe ehrliche Begrenzung wie im Flagship-Fair-Value-Simulator
 * (§B.1-Erweiterung).
 *
 * Historische Punkte je Serie durchgezogen, Prognosezone (grau hinterlegt) gestuft
 * (`type="stepBefore"`, da diskrete Geschaeftsjahres-Spruenge, bewusste Ausnahme von der
 * Sonst-"linear"-Regel): echte Analystenjahre durchgezogen-transparent, extrapolierte
 * Jahre feiner gestrichelt - nie ohne Kennzeichnung vermischt (backlog.md -> §0.3).
 *
 * BUGFIX (2026-07-17, Runde 2 Punkt 10, "Jahr 2027 nicht vollstaendig"): jeder
 * Schaetzjahr-Punkt sitzt am 31.12. des Jahres, das er repraesentiert (z.B. 2027-12-31
 * fuer die 2027er-Schaetzung). Mit `stepAfter` (Wert bleibt bis zum naechsten Punkt
 * beim ALTEN Stand, springt erst GENAU am Zielpunkt um) faellt der komplette Zeitraum
 * eines Jahres auf den WERT DES VORJAHRS - das echte 2027er-Multiple erschien dadurch
 * nur als einzelner Punkt exakt am rechten Chart-Rand, keine sichtbare Flaeche/Linie
 * fuer 2027 selbst. `stepBefore` springt stattdessen sofort nach dem Vorjahres-Punkt auf
 * den neuen Wert und haelt ihn bis zum naechsten Stichtag - dadurch zeigt die Flaeche
 * "waehrend 2027" tatsaechlich die 2027er-Schaetzung (FASTgraphs-Konvention, siehe
 * Klassenkommentar oben), nicht mehr die von 2026 fortgeschriebene.
 */
export function DailyMultiplesChart({
  priceHistory,
  series,
  range,
  onRangeChange,
}: {
  priceHistory: PricePoint[];
  series: DailyMultipleSeriesInput[];
  /** Vom Elternteil kontrolliert (siehe ValuationTabCharts) - eigener, unabhaengiger
   * Zeitraum-State (backlog.md -> [Punkt 50]: "die Zeitraum-Auswahl von 'Fairer Wert &
   * Kaufsimulator' soll unabhängig von der Zeitraum-Auswahl bei 'Bewertungskennzahlen im
   * Verlauf' sein"). Loest eine fruehere, gegenteilige Zusammenlegung ab, siehe
   * ausfuehrlicher Kommentar in valuation-tab-charts.tsx. */
  range: ChartRangeLabel;
  onRangeChange: (range: ChartRangeLabel) => void;
}) {
  const [visible, setVisible] = useState<Set<string>>(() => new Set(series.map((s) => s.key)));

  function toggle(key: string) {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size === 1) return prev;
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  const filteredPrice = useMemo(() => filterDateRange(priceHistory, range), [priceHistory, range]);
  const renderedPrice = useMemo(
    () => withTimeField(downsample(filteredPrice, 1000, (p) => p.close)),
    [filteredPrice],
  );
  const lastPrice = priceHistory[priceHistory.length - 1];
  const lastPriceTime = renderedPrice[renderedPrice.length - 1]?.time;

  // Tagesgenaue Multiple-Reihe je Kennzahl EINMAL berechnet (nicht der volle, sondern
  // der ungefilterte Verlauf - der Bewertungszeitraum-Filter greift erst danach, sowohl
  // fuer den Chart als auch fuer den Ø-Wert, damit beide konsistent bleiben). Wird von
  // chartData UND dem Ø-Wert unten geteilt, statt zweimal berechnet zu werden.
  const dailyMultipleBySeries = useMemo(() => {
    const map = new Map<string, { date: string; multiple: number }[]>();
    for (const s of series) {
      const steps = s.metricSteps.map((m) => ({ date: `${m.year}-12-31`, value: m.value }));
      // TTM-Bruecke (siehe currentMetricValue-Kommentar oben): ein zusaetzlicher Punkt
      // exakt am letzten echten Kurstag statt am Jahresende, damit der aktuellste
      // Chart-Punkt denselben TTM-Standard wie der Fairer-Wert-Simulator nutzt.
      if (s.currentMetricValue !== undefined && lastPrice) {
        steps.push({ date: lastPrice.date, value: s.currentMetricValue });
      }
      map.set(
        s.key,
        computeDailyMultiple(
          priceHistory.map((p) => ({ date: p.date, value: p.close })),
          steps,
        ),
      );
    }
    return map;
  }, [series, priceHistory, lastPrice]);

  const perSeriesData = useMemo(() => {
    return series.map((s) => {
      const dailyMultiple = dailyMultipleBySeries.get(s.key) ?? [];
      const rangeFiltered = filterDateRange(
        dailyMultiple.map((d) => ({ date: d.date, close: d.multiple }) as PricePoint),
        range,
      ).map((p) => ({ date: p.date, multiple: p.close }));
      const average = computeAverageMultiple(rangeFiltered);
      const forecast =
        lastPrice && lastPrice.close > 0
          ? computeForwardMultiplePath(lastPrice.close, s.forwardSteps, s.longTermGrowthRate, FORECAST_YEARS)
          : [];
      return { key: s.key, average, forecast };
    });
  }, [series, dailyMultipleBySeries, range, lastPrice]);

  const chartData = useMemo(() => {
    const rows = new Map<number, DailyChartRow>();
    function upsert(time: number, patch: Partial<DailyChartRow>) {
      rows.set(time, { ...(rows.get(time) ?? { time }), ...patch });
    }

    for (const s of series) {
      if (!visible.has(s.key)) continue;
      const dailyMultiple = dailyMultipleBySeries.get(s.key) ?? [];
      const rangeFiltered = filterDateRange(
        dailyMultiple.map((d) => ({ date: d.date, close: d.multiple }) as PricePoint),
        range,
      );
      const rendered = withTimeField(downsample(rangeFiltered, 1000, (p) => p.close));
      for (const p of rendered) {
        upsert(p.time, { [s.key]: p.close });
      }
    }

    for (const { key, forecast } of perSeriesData) {
      if (!visible.has(key) || forecast.length === 0 || lastPriceTime === undefined) continue;
      const realPoints = forecast.filter((p) => p.source === "analyst");
      const extraPoints = forecast.filter((p) => p.source === "extrapolated");

      // Anker am letzten echten Kurspunkt ("heute"), damit die Prognoselinie luecken-
      // los an die historische Linie anschliesst - gleiches Prinzip wie in
      // fair-value-simulator.tsx.
      const todayMultiple = rows.get(lastPriceTime)?.[key];
      if (realPoints.length > 0) {
        if (todayMultiple !== undefined) upsert(lastPriceTime, { [`${key}__real`]: todayMultiple });
        for (const p of realPoints) {
          upsert(Date.UTC(p.year, 11, 31), { [`${key}__real`]: p.multiple });
        }
      }
      if (extraPoints.length > 0) {
        // Anker am letzten ECHTEN Prognosepunkt (nicht "heute"), damit die gestrichelte
        // Extrapolation dort nahtlos weiterlaeuft, wo die echten Schaetzjahre enden.
        const lastReal = realPoints[realPoints.length - 1];
        if (lastReal) {
          upsert(Date.UTC(lastReal.year, 11, 31), { [`${key}__extra`]: lastReal.multiple });
        } else if (todayMultiple !== undefined) {
          upsert(lastPriceTime, { [`${key}__extra`]: todayMultiple });
        }
        for (const p of extraPoints) {
          upsert(Date.UTC(p.year, 11, 31), { [`${key}__extra`]: p.multiple });
        }
      }
    }

    return [...rows.values()].sort((a, b) => a.time - b.time);
  }, [series, dailyMultipleBySeries, range, visible, perSeriesData, lastPriceTime]);

  const lastForecastTime = useMemo(() => {
    const times = perSeriesData
      .filter(({ key }) => visible.has(key))
      .flatMap(({ forecast }) => forecast)
      .map((p) => Date.UTC(p.year, 11, 31));
    return times.length > 0 ? Math.max(...times) : undefined;
  }, [perSeriesData, visible]);

  const isNarrow = useIsNarrowViewport();
  // Ticks aus chartData (den tatsaechlich gerenderten Zeilen) statt aus renderedPrice (der
  // vollen Kurshistorie) berechnen - die Multiples-Zeitreihe beginnt erst dort, wo die
  // jaehrlichen Fundamentaldaten (EPS/Umsatz je Aktie) einsetzen, oft Jahrzehnte nach dem
  // Kursstart bei "Max". Ticks aus der laengeren Kurshistorie lagen dadurch links vom
  // XAxis-domain=["dataMin","dataMax"] von chartData und wurden von Recharts stillschweigend
  // verworfen (backlog.md -> Feedback-Runde 2026-07-17, Punkt 7). Gleiches Prinzip wie
  // MultiKpiLineChart oben, das seine Ticks bereits korrekt aus der eigenen gerenderten
  // Serie berechnet.
  const axisTicks = useMemo(() => {
    const asDates = chartData.map((d) => ({ date: new Date(d.time).toISOString().slice(0, 10) }));
    return computePriceAxisTicks(asDates, range, isNarrow ? 5 : 8).map((d) => Date.parse(d));
  }, [chartData, range, isNarrow]);

  const visibleSeries = series.filter((s) => visible.has(s.key));
  const hasForecast = perSeriesData.some(({ key, forecast }) => visible.has(key) && forecast.length > 0);
  const visibleNotes = [...new Set(visibleSeries.map((s) => s.note).filter((n): n is string => !!n))];

  if (priceHistory.length === 0 || series.every((s) => s.metricSteps.length === 0)) {
    return <p className="text-sm text-muted-foreground">Für diese Kennzahlen liegt keine Historie vor.</p>;
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {series.map((s) => (
            <SeriesTogglePill key={s.key} label={s.label} color={s.color} active={visible.has(s.key)} onClick={() => toggle(s.key)} />
          ))}
        </div>
        <RangeSelector value={range} onChange={onRangeChange} />
      </div>
      <div className="h-72 w-full [&_svg]:outline-none [&_svg]:focus:outline-none">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 8, right: 24, left: 0, bottom: 0 }}
            accessibilityLayer={false}
          >
            <CartesianGrid stroke="#16265C" strokeOpacity={0.12} strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="time"
              type="number"
              domain={["dataMin", "dataMax"]}
              scale="time"
              tickFormatter={(v: number) => formatPriceAxisTick(v, range)}
              ticks={axisTicks}
              interval={0}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              axisLine={{ stroke: "#ECEAE3" }}
              tickLine={false}
            />
            <YAxis
              domain={["auto", "auto"]}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              tickFormatter={(v: number) => formatRatio(v)}
              width={56}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              formatter={(value, name) => {
                const key = String(name).replace(/__real$|__extra$/, "");
                const s = series.find((item) => item.key === key);
                if (!s) return [null, null];
                const suffix = String(name).endsWith("__extra") ? " (Extrapolation)" : String(name).endsWith("__real") ? " (Schätzung)" : "";
                return [formatRatio(Number(value)), s.label + suffix];
              }}
              labelFormatter={(label) => new Date(Number(label)).toLocaleDateString("de-DE", { year: "numeric", month: "short", day: "2-digit" })}
              cursor={{ stroke: "#16265C", strokeOpacity: 0.25, strokeWidth: 1, strokeDasharray: "3 3" }}
              contentStyle={{ borderRadius: 4, borderColor: "#ECEAE3", fontSize: 13, boxShadow: "0 4px 16px rgba(22,38,92,0.08)" }}
            />
            {hasForecast && lastPriceTime !== undefined && lastForecastTime !== undefined && lastForecastTime > lastPriceTime && (
              <ReferenceArea
                x1={lastPriceTime}
                x2={lastForecastTime}
                fill="#16265C"
                fillOpacity={0.05}
                label={{
                  value: "Prognosezone",
                  position: "insideTopLeft",
                  fill: "#6B7280",
                  fontSize: 10,
                }}
              />
            )}
            {/* Vertikal gestaffelte Labels (backlog.md -> [Punkt 50], "Durchschnittslinien
                deutlicher... wenn sie eng beieinander liegen") - liegen z.B. Ø-KGV und
                Ø-KUV numerisch nah beieinander, landeten ihre Labels vorher exakt
                uebereinander an derselben "insideTopRight"-Ecke und wurden dadurch
                gegenseitig unleserlich. `dy`-Versatz nach sichtbarem Rendering-Index
                (nicht dem vollen series-Array, das wuerde bei ausgeblendeten Serien
                Luecken in der Staffelung reissen) haelt sie auch bei identischem Wert
                lesbar untereinander statt exakt uebereinander. */}
            {perSeriesData
              .filter(({ key, average }) => visible.has(key) && average !== undefined)
              .map(({ key, average }, index) => {
                const s = series.find((item) => item.key === key)!;
                return (
                  <ReferenceLine
                    key={`avg-${key}`}
                    y={average}
                    stroke={s.color}
                    strokeOpacity={0.55}
                    strokeDasharray="5 4"
                    label={{
                      value: `Ø ${s.label}: ${formatRatio(average!)}`,
                      position: "insideTopRight",
                      dy: index * 12,
                      fill: s.color,
                      fillOpacity: 0.85,
                      fontSize: 10,
                    }}
                  />
                );
              })}
            {visibleSeries.map((s) => (
              <Line
                key={s.key}
                type="linear"
                dataKey={s.key}
                stroke={s.color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={false}
                activeDot={{ r: 5, fill: s.color, stroke: "#FAF9F5", strokeWidth: 2.5 }}
                isAnimationActive={false}
                connectNulls
                style={LINE_SHADOW_STYLE}
              />
            ))}
            {visibleSeries.map((s) => (
              <Line
                key={`${s.key}__real`}
                type="stepBefore"
                dataKey={`${s.key}__real`}
                stroke={s.color}
                strokeOpacity={0.7}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            ))}
            {visibleSeries.map((s) => (
              <Line
                key={`${s.key}__extra`}
                type="stepBefore"
                dataKey={`${s.key}__extra`}
                stroke={s.color}
                strokeOpacity={0.5}
                strokeWidth={1.5}
                strokeDasharray="3 3"
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {hasForecast && (
        <p className="mt-2 text-xs text-muted-foreground">
          Graue Zone: Prognose auf Basis von Analystenschätzungen (durchgezogen-transparent) bzw.
          wachstumsbasierter Extrapolation (fein gestrichelt) - keine Kursprognose, rein illustrativ.
        </p>
      )}
      {visibleNotes.map((note) => (
        <p key={note} className="mt-1 text-xs text-muted-foreground">
          {note}
        </p>
      ))}
    </div>
  );
}
